<script setup lang="ts">
// One row of a library list (HRTH-164.2, 164.7), like a track row in Spotify or an iPod
// menu item: tapping the row plays or opens it, and the ⋯ at its end holds everything else
// (ActionSheet); menu rows (`nav`) end in › instead. A cover goes in the `art` slot.
import Icon from './Icon.vue'

defineProps<{
  title: string
  subtitle?: string
  /** Leading column: a track number, or a kind (DIR, IMG). */
  lead?: string
  /** Trailing detail: duration or size. */
  detail?: string
  art?: string | null
  playing?: boolean
  disabled?: boolean
  /** Hide the ⋯ (folders). */
  noMore?: boolean
  /** A menu row: leads somewhere (›), no ⋯. */
  nav?: boolean
  /** Choosing several: a check box in place of the lead. */
  selecting?: boolean
  selected?: boolean
}>()
const emit = defineEmits<{ open: []; more: [] }>()
</script>

<template>
  <div class="group flex min-h-12 items-center border-b border-border hover:bg-bg-hover md:min-h-11" :class="playing ? 'bg-bg-hover' : ''">
    <button
      class="flex min-w-0 flex-1 items-center gap-3 self-stretch py-1.5 pl-1 text-left disabled:cursor-default"
      :disabled="disabled"
      data-testid="row-open"
      @click="emit('open')"
    >
      <span v-if="selecting" class="flex w-8 shrink-0 justify-center">
        <span class="flex h-5 w-5 items-center justify-center border border-ink" :class="selected ? 'bg-ink text-paper' : ''">
          <Icon v-if="selected" name="check" />
        </span>
      </span>
      <span v-else-if="$slots.art" class="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden border border-border">
        <slot name="art" />
      </span>
      <span v-else-if="art !== undefined" class="field-grid flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden border border-border text-text-muted">
        <img v-if="art" :src="art" alt="" class="h-full w-full object-cover" />
        <span v-else class="mono">{{ lead ?? '♪' }}</span>
      </span>
      <span v-else-if="lead !== undefined" class="mono w-8 shrink-0 text-center" :class="playing ? 'text-text-primary' : 'text-text-muted'">
        <template v-if="playing">▶</template>
        <template v-else>{{ lead }}</template>
      </span>
      <span class="min-w-0 flex-1">
        <span class="block truncate text-sm" :class="playing ? 'text-text-primary' : 'text-text-secondary group-hover:text-text-primary'" data-testid="row-title">{{ title }}</span>
        <span v-if="subtitle" class="mono block truncate text-text-muted">{{ subtitle }}</span>
      </span>
      <span v-if="detail" class="mono shrink-0 pr-1 text-text-muted tabular-nums">{{ detail }}</span>
      <span v-if="nav" class="flex w-8 shrink-0 justify-center text-text-muted" aria-hidden="true"><Icon name="right" :size="5" /></span>
    </button>
    <slot />
    <button
      v-if="!noMore && !nav && !selecting"
      class="flex h-11 w-11 shrink-0 items-center justify-center text-text-muted hover:text-text-primary md:h-9 md:w-9"
      :aria-label="`More for ${title}`"
      data-testid="row-more"
      @click="emit('more')"
    >
      <Icon name="more" :size="5" />
    </button>
  </div>
</template>
